import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@core/store';

interface AuthGuardProps {
    children: ReactNode;
}

/**
 * Auth Guard Component
 *
 * Protects routes that require user authentication.
 * Redirects unauthenticated users to the login page.
 * Preserves the intended destination for redirect after login.
 */
export function AuthGuard({ children }: AuthGuardProps) {
    const location = useLocation();
    const { isUserAuthenticated, isLoading } = useAppSelector((state) => state.auth);

    // Show nothing while checking auth state
    if (isLoading) {
        return null;
    }

    // Redirect to login if not authenticated
    if (!isUserAuthenticated) {
        return <Navigate to="/authentication" state={{ from: location }} replace />;
    }

    return <>{children}</>;
}

export default AuthGuard;
